import { Component, AfterViewInit, OnInit, OnDestroy } from '@angular/core';

declare const lucide: any;

@Component({
  selector: 'app-legacy-system-migration',
  templateUrl: './legacy-system-migration.component.html',
  styleUrls: ['./legacy-system-migration.component.css']
})
export class LegacySystemMigrationComponent implements OnInit, AfterViewInit, OnDestroy {
  currentStep = 0;
  autoScrollInterval: any;

  // Why Modernize Legacy Systems data
  legacyProblems = [
    {
      icon: 'trending-down',
      title: 'Limited Scalability & Integration',
      text: 'Legacy systems often cannot scale with modern business needs and lack integration capabilities with contemporary tools and platforms.',
      impact: '75% of organizations report integration challenges',
      stat: 'Scalability Issues'
    },
    {
      icon: 'dollar-sign',
      title: 'Rising Maintenance Costs & Security Risks',
      text: 'Older systems require more resources to maintain, leading to higher operational costs and increased security vulnerabilities that expose businesses to cyber threats.',
      impact: 'Organizations spend 60-80% of IT budget on maintenance',
      stat: 'Cost Overruns'
    },
    {
      icon: 'clock',
      title: 'Frequent Downtime & Operational Inefficiencies',
      text: 'Outdated systems are prone to failures, causing operational downtime and reduced productivity across the organization, impacting customer satisfaction.',
      impact: 'Average downtime costs $5,600 per minute',
      stat: 'Productivity Loss'
    },
    {
      icon: 'lightbulb',
      title: 'Barriers to Innovation & Digital Transformation',
      text: 'Legacy systems create barriers to digital transformation initiatives and prevent adoption of cutting-edge technologies like AI, cloud computing, and IoT.',
      impact: '89% struggle with legacy system modernization',
      stat: 'Innovation Gap'
    }
  ];

  // Migration Methodology steps
  methodologySteps = [
    {
      step: '01',
      title: 'System Assessment & Strategy',
      description: 'Analyze legacy environments, risks, and readiness.',
      icon: 'search'
    },
    {
      step: '02', 
      title: 'Database Schema & Structure Conversion',
      description: 'Transform legacy databases (DB2, Oracle, VSAM) to modern SQL/PostgreSQL/MySQL.',
      icon: 'database'
    },
    {
      step: '03',
      title: 'Data Cleansing & Normalization',
      description: 'Standardize data for consistency and accuracy.',
      icon: 'shield-check'
    },
    {
      step: '04',
      title: 'Automated Data Migration & Validation',
      description: 'AI-driven migration with rollback capabilities.',
      icon: 'zap'
    },
    {
      step: '05',
      title: 'Data Security & Compliance',
      description: 'Encrypt data (in-use, at-rest, in-transit) to meet GDPR, HIPAA, FedRAMP standards.',
      icon: 'lock'
    },
    {
      step: '06',
      title: 'Integration & Testing',
      description: 'Ensure connectivity with ERP, analytics, APIs, and modern apps.',
      icon: 'link'
    },
    {
      step: '07',
      title: 'Phased Deployment & Support',
      description: 'Roll out incrementally with full training and post-migration support.',
      icon: 'rocket'
    }
  ];

  // Benefits of Migration
  migrationBenefits = [
    {
      icon: 'trending-down',
      title: 'Reduced Costs',
      description: 'Lower maintenance expenses and minimize downtime through modern, efficient systems.'
    },
    {
      icon: 'shield',
      title: 'Enhanced Security',
      description: 'Modern platforms with advanced cybersecurity features and compliance capabilities.'
    },
    {
      icon: 'zap',
      title: 'Improved Efficiency',
      description: 'Faster, more reliable, and user-friendly systems that boost productivity.'
    },
    {
      icon: 'cpu',
      title: 'Future-Ready Technology',
      description: 'Seamless integration with digital innovations and emerging technologies.'
    },
    {
      icon: 'refresh-cw',
      title: 'Business Continuity',
      description: 'Preserve essential data and processes during migration with zero disruption.'
    },
    {
      icon: 'bar-chart',
      title: 'Better Analytics & Insights',
      description: 'Access real-time data analytics, advanced reporting, and actionable business insights for informed decision-making.'
    }
  ];

  // Technologies we work with
  technologies = [
    {
      category: 'Cloud Platforms',
      items: ['AWS', 'Azure', 'Google Cloud'],
      icon: 'cloud'
    },
    {
      category: 'Databases',
      items: ['Oracle', 'SQL Server', 'PostgreSQL', 'MySQL'],
      icon: 'database'
    },
    {
      category: 'Frameworks',
      items: ['.NET', 'Java', 'Python', 'Node.js'],
      icon: 'code'
    },
    {
      category: 'Integration Tools',
      items: ['APIs', 'Web Services', 'Microservices Architecture'],
      icon: 'link'
    }
  ];

  // Analytics Integration benefits
  analyticsBenefits = [
    {
      icon: 'trending-up',
      title: 'Predictive forecasting for demand, supply, and risks',
      description: 'Advanced analytics enable accurate forecasting and risk assessment.'
    },
    {
      icon: 'brain',
      title: 'Prescriptive decision-making with AI-driven recommendations',
      description: 'AI-powered insights guide optimal business decisions and strategies.'
    },
    {
      icon: 'shield-alert',
      title: 'Preventive safeguards for security and compliance risks',
      description: 'Intelligent monitoring and automated safeguards protect your business.'
    }
  ];

  constructor() { }

  ngOnInit(): void {
    // Initialize any required functionality
  }

  openDemoPopup(): void {
    // Navigate to contact page or open demo popup
    window.location.href = '/contact-us';
  }

  goToStep(stepIndex: number): void {
    this.currentStep = stepIndex;
    const track = document.querySelector('.methodology-stepper-track') as HTMLElement;
    if (track) {
      const stepWidth = 300; // Width of each step including margin
      const scrollPosition = stepIndex * stepWidth;
      
      // Stop auto-scroll when user manually navigates
      this.stopAutoScroll();
      
      track.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      
      // Restart auto-scroll after a delay
      setTimeout(() => {
        this.startAutoScroll();
      }, 5000);
    }
  }

  startAutoScroll(): void {
    this.autoScrollInterval = setInterval(() => {
      const track = document.querySelector('.methodology-stepper-track') as HTMLElement;
      if (track) {
        const stepWidth = 300;
        const currentScroll = track.scrollLeft;
        const maxScroll = track.scrollWidth - track.clientWidth;
        
        // Move to next step
        this.currentStep = (this.currentStep + 1) % this.methodologySteps.length;
        
        // If we're at the end of first set, reset to beginning seamlessly
        if (currentScroll >= maxScroll - stepWidth) {
          // Instantly jump to beginning without animation
          track.scrollLeft = 0;
          // Then continue with smooth scroll
          setTimeout(() => {
            track.scrollTo({
              left: stepWidth,
              behavior: 'smooth'
            });
          }, 50);
        } else {
          // Normal smooth scroll to next step
          track.scrollTo({
            left: currentScroll + stepWidth,
            behavior: 'smooth'
          });
        }
      }
    }, 3000); // Auto scroll every 3 seconds for smoother infinite effect
  }

  stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }

  ngAfterViewInit(): void {
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }

    // Re-initialize icons after a short delay to ensure DOM is ready
    setTimeout(() => {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }, 100);

    const animatedElements: NodeListOf<HTMLElement> = document.querySelectorAll('[data-animate]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          el.classList.add('in-view');
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -10% 0px' });

    animatedElements.forEach(el => observer.observe(el));

    // Start auto-scrolling after a delay
    setTimeout(() => {
      this.startAutoScroll();
    }, 2000);
  }

  ngOnDestroy(): void {
    this.stopAutoScroll();
  }

}
